import { useRef, useState } from "react";
import { Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import trailer from "@/assets/founders-trailer-final.mp4.asset.json";
import poster from "@/assets/trailer-poster-clean.jpg.asset.json";

export function Trailer() {
  const [started, setStarted] = useState(false);
  const ref = useRef<HTMLVideoElement>(null);

  const start = () => {
    setStarted(true);
    requestAnimationFrame(() => {
      const el = ref.current;
      if (!el) return;
      el.muted = false;
      void el.play().catch(() => {
        el.muted = true;
        void el.play().catch(() => undefined);
      });
    });
  };

  return (
    <section className="relative bg-background py-16 sm:py-24">
      <div className="mx-auto w-full max-w-5xl px-6 sm:px-10">
        <div className="max-w-2xl">
          <p className="micro-label">Inside the cockpit · 0:31</p>
          <h2 className="etched mt-5 pb-1 text-3xl leading-[1.15] font-semibold sm:text-5xl">
            Your next move is already waiting.
          </h2>
          <p className="mt-5 max-w-xl text-sm leading-relaxed text-muted-foreground sm:text-base">
            See the lock, the daily ritual, the private history—and the moment it starts to feel like yours.
          </p>
        </div>

        <div className="mt-10 overflow-hidden rounded-lg border border-border/60 bg-card shadow-[0_24px_60px_-28px_rgba(0,0,0,0.35)]">
          <div className="relative aspect-video w-full">
            {started ? (
              <video
                ref={ref}
                className="absolute inset-0 h-full w-full object-cover"
                src={trailer.url}
                poster={poster.url}
                controls
                playsInline
                preload="auto"
              />
            ) : (
              <Button
                type="button"
                variant="ghost"
                onClick={start}
                aria-label="Play the 31-second trailer"
                className="group absolute inset-0 h-full w-full cursor-pointer rounded-none p-0 hover:bg-transparent"
              >
                <img
                  src={poster.url}
                  alt="Preview The Founders App trailer"
                  className="absolute inset-0 h-full w-full object-cover"
                  loading="lazy"
                  decoding="async"
                  width={1920}
                  height={1080}
                />
                <span className="absolute inset-0 bg-background/20 transition-colors duration-300 group-hover:bg-background/10" />
                <span className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-background/70 to-transparent" />
                <span className="micro-label absolute right-4 bottom-4 z-10 border border-border bg-background/90 px-2.5 py-1.5 text-foreground sm:right-6 sm:bottom-6">
                  0:31
                </span>
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="relative flex h-20 w-20 items-center justify-center sm:h-24 sm:w-24">
                    <span className="trailer-play-ring absolute inset-0 rounded-full border border-primary/40" aria-hidden="true" />
                    <span className="trailer-play-ring absolute inset-0 rounded-full border border-primary/30 [animation-delay:0.8s]" aria-hidden="true" />
                    <span className="trailer-play relative flex h-full w-full items-center justify-center rounded-full border border-primary bg-primary text-primary-foreground shadow-2xl transition-transform duration-300 group-hover:scale-110">
                      <Play className="h-7 w-7 translate-x-[2px] fill-current sm:h-9 sm:w-9" />
                    </span>
                  </span>
                </span>
              </Button>
            )}
          </div>
        </div>

        <p className="mt-5 text-sm text-muted-foreground">
          Real screens. Sample business, not anyone&rsquo;s data.
        </p>
      </div>
    </section>
  );
}

